import type { AxiosInstance } from 'axios';
import { DASHBOARD_CONFIG } from '@/config/dashboard';
import type {
  ClassificationInput,
  ClassificationResponse,
} from '../../domain/entities/sinarem-report.entity';
import type { GeneralReport } from '../../domain/entities/general-report.entity';
import type { QuestionDistribution } from '../../domain/entities/question-distribution.entity';
import type { QuestionList } from '../../domain/entities/question-list.entity';
import type { AreaComparison } from '../../domain/entities/area-comparison.entity';
import type { ThemeComparison } from '../../domain/entities/theme-comparison.entity';
import type { StudentRanking } from '../../domain/entities/student-ranking.entity';
import type { StudentDetail } from '../../domain/entities/student-detail.entity';

export class SinaremReportsApiDatasource {
  private readonly client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  async getClassification(
    input?: ClassificationInput,
  ): Promise<ClassificationResponse> {
    const { data } = await this.client.post<ClassificationResponse>(
      '/v1/mockexamperformancedashboard/classification',
      input?.emails ? { emails: input.emails } : undefined,
    );
    return data;
  }

  async getGeneralReport(
    editionId: string,
    includeExtra?: boolean,
  ): Promise<GeneralReport> {
    const { data } = await this.client.get<{ report: GeneralReport }>(
      '/v1/mockexamperformancedashboard/general',
      {
        params: {
          editionId,
          includeExtra,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );
    return data.report;
  }

  async getQuestionDistribution(
    editionId: string,
    includeExtra?: boolean,
  ): Promise<QuestionDistribution> {
    const { data } = await this.client.get<{ report: QuestionDistribution }>(
      '/v1/mockexamperformancedashboard/question-distribution',
      {
        params: {
          editionId,
          includeExtra,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );
    return data.report;
  }

  async getQuestionList(
    editionId: string,
    includeExtra?: boolean,
  ): Promise<QuestionList> {
    const { data } = await this.client.get<QuestionList>(
      '/v1/mockexamperformancedashboard/question-list',
      {
        params: {
          editionId,
          includeExtra,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );
    return data;
  }

  async getAreaComparison(editionId: string): Promise<AreaComparison> {
    const { data } = await this.client.get<{ report: AreaComparison }>(
      '/v1/mockexamperformancedashboard/area-comparison',
      { params: { editionId, courseId: DASHBOARD_CONFIG.courseId } },
    );
    return data.report;
  }

  async getThemeComparison(editionId: string): Promise<ThemeComparison> {
    const { data } = await this.client.get<{ report: ThemeComparison }>(
      '/v1/mockexamperformancedashboard/theme-comparison',
      { params: { editionId, courseId: DASHBOARD_CONFIG.courseId } },
    );
    return data.report;
  }

  async getStudentRanking(
    editionId: string,
    page: number,
    pageSize: number,
  ): Promise<StudentRanking> {
    const { data } = await this.client.get<StudentRanking>(
      '/v1/mockexamperformancedashboard/student-ranking',
      {
        params: {
          editionId,
          page,
          pageSize,
          courseId: DASHBOARD_CONFIG.courseId,
        },
      },
    );
    return data;
  }

  async getStudentDetail(
    editionId: string,
    email: string,
  ): Promise<StudentDetail> {
    const { data } = await this.client.get<{ report: StudentDetail }>(
      '/v1/mockexamperformancedashboard/student-detail',
      {
        params: { editionId, email, courseId: DASHBOARD_CONFIG.courseId },
      },
    );
    return data.report;
  }
}
